// The signed-in frame: sidebar navigation, top bar with breadcrumbs and the live-connection indicator.
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../api';
import { useAuth } from '../auth';
import { useLive } from '../live';
import { Icon, ago, initials } from '../ui';
import { ROLE_LABELS, type AppNotification } from '../types';
import { useTasksDue } from './Tasks';
import { SearchBox } from './Search';

type Crumb = { label: string; to?: string };
const CrumbContext = createContext<(c: Crumb[]) => void>(() => {});
export const useCrumbs = () => useContext(CrumbContext);

export function Shell() {
  const { me, can, signOut } = useAuth();
  const live = useLive();
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const tasksDue = useTasksDue(!!me?.is_staff && can('crm.view'));
  // A new page starts at the top; Back keeps where you were.
  const { pathname } = useLocation();
  const navType = useNavigationType();
  useEffect(() => { if (navType !== 'POP') window.scrollTo(0, 0); }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!me) return null;
  return (
    <CrumbContext.Provider value={setCrumbs}>
      <div className="shell">
        <aside className="sidebar">
          <img className="logo" src="/assets-static/logo.png" alt="Blue Market" />
          <nav className="nav">
            {me.is_staff && can('delivery.view') && <NavLink to="/projects"><Icon name="folder" />Projects</NavLink>}
            {can('clients.view') && <NavLink to="/clients"><Icon name="clients" />Clients</NavLink>}
            {me.is_staff && can('crm.view') && <NavLink to="/pipeline"><Icon name="pipeline" />Pipeline</NavLink>}
            {me.is_staff && (can('crm.view') || can('delivery.view')) && <NavLink to="/reports"><Icon name="chart" />Reports</NavLink>}
            {me.is_staff && can('crm.view') && <NavLink to="/tasks"><Icon name="check" />My tasks{tasksDue > 0 && <span className="nav-count">{tasksDue}</span>}</NavLink>}
            {!me.is_staff && me.client && <NavLink to="/" end className={({ isActive }) => isActive || location.pathname.startsWith('/projects') ? 'active' : ''}><Icon name="folder" />{me.client.name}</NavLink>}
            {(can('users.manage') || can('users.invite_client') || can('audit.view_all') || can('audit.view_client')) && <div className="nav-label">Admin</div>}
            {(can('users.manage') || can('users.invite_client')) && <NavLink to="/admin/users"><Icon name="users" />People</NavLink>}
            {(can('audit.view_all') || can('audit.view_client')) && <NavLink to="/admin/audit"><Icon name="audit" />Audit log</NavLink>}
            {me.is_staff && can('backups.manage') && <NavLink to="/admin/backups"><Icon name="shield" />Backups</NavLink>}
          </nav>
          <div className="me">
            <NavLink to="/account" className="me-link grow" title="Your account">
              <div className="avatar">{initials(me.name)}</div>
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="small" style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{me.name}</div>
                <div className="tiny muted">{ROLE_LABELS[me.role]}{me.client ? ` · ${me.client.name}` : ''}</div>
              </div>
            </NavLink>
            <button className="btn ghost sm" onClick={signOut} title="Sign out" aria-label="Sign out"><Icon name="out" /></button>
          </div>
        </aside>
        <div className="main">
          <header className="topbar">
            <nav className="crumbs grow" aria-label="Breadcrumb">
              {crumbs.map((c, i) => (
                <span key={i} className="row" style={{ gap: 8, minWidth: 0 }}>
                  {i > 0 && <span>/</span>}
                  {c.to && i < crumbs.length - 1 ? <NavLink to={c.to}>{c.label}</NavLink> : <b>{c.label}</b>}
                </span>
              ))}
            </nav>
            <SearchBox />
            <Bell />
            <span className={`live ${live.connected ? '' : 'off'}`} title={live.connected ? 'Changes by others appear instantly' : 'Reconnecting…'}>
              <span className="dot" />{live.connected ? 'Live' : 'Reconnecting'}
            </span>
          </header>
          <Outlet />
        </div>
      </div>
    </CrumbContext.Provider>
  );
}

export function Page({ title, eyebrow, actions, children }: { title: string; eyebrow?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <main className="page">
      <div className="page-head">
        <div>{eyebrow && <div className="eyebrow">{eyebrow}</div>}<h1>{title}</h1></div>
        {actions && <div className="row wrap">{actions}</div>}
      </div>
      {children}
    </main>
  );
}

// ---------- the bell ----------

function Bell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['notifications'], queryFn: () => get<{ notifications: AppNotification[]; unread: number }>('/api/notifications'), refetchInterval: 120_000 });
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!(e.target as HTMLElement).closest('.bell')) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    addEventListener('mousedown', close); addEventListener('keydown', esc);
    return () => { removeEventListener('mousedown', close); removeEventListener('keydown', esc); };
  }, [open]);
  const read = async (body: { ids?: string[]; all?: boolean }) => { qc.setQueryData(['notifications'], await post('/api/notifications/read', body)); };
  const go = (n: AppNotification) => { if (!n.read) read({ ids: [n.id] }); setOpen(false); navigate(n.url); };
  const unread = data?.unread ?? 0;
  return (
    <div className="bell">
      <button className="btn ghost sm bell-btn" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={unread ? `${unread} unread notifications` : 'Notifications'}>
        <Icon name="bell" />{unread > 0 && <span className="bell-count">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="bell-pop" role="dialog" aria-label="Notifications">
          <div className="bell-head"><b className="small grow">Notifications</b>{unread > 0 && <button className="link-btn tiny" onClick={() => read({ all: true })}>Mark all read</button>}</div>
          <div className="bell-list">
            {(data?.notifications ?? []).map((n) => (
              <button key={n.id} className={`bell-item ${n.read ? '' : 'unread'}`} onClick={() => go(n)}>
                <span className="small">{n.title}</span>
                {n.excerpt && <span className="tiny muted bell-excerpt">{n.excerpt}</span>}
                <span className="tiny muted">{ago(n.created_at)}</span>
              </button>
            ))}
            {!data?.notifications.length && <div className="empty small">Nothing yet. You'll see mentions, assignments and replies here.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
